/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { breakpoints, mq } from '@stories/theming/settings';

type ImgDef = {
  ratio?: number;
  src: string;

  alt?: string;
  cssProps?: object;
  sizes?: string;
  srcset?: string;
};

type SourceDef = {
  media?: string;
  sizes: string;
  srcSet: string;
};

type PictureDef = {
  img: ImgDef;
  ratio: {
    [key: number]: number;
  };
  sources?: SourceDef[];
};

const Image: React.FC<ImgDef> = ({ src, cssProps, alt = '', ratio = 1, srcset, sizes }) => {
  return (
    <div
      css={{
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        '::before': {
          content: '""',
          display: 'block',
          paddingBottom: `${ratio * 100}%`
        },
        ...cssProps
      }}
    >
      {/*eslint-disable-next-line @next/next/no-img-element*/}
      <img
        css={{ position: 'absolute', left: 0, top: 0, width: '100%' }}
        alt={alt}
        src={src}
        sizes={sizes}
        srcSet={srcset}
      />
    </div>
  );
};

const Picture: React.FC<PictureDef> = ({ sources = [], img, ratio }) => {
  const bp = ['base', ...Object.keys(breakpoints)].map((breakpointName) =>
    ratio[breakpointName] ? `${ratio[breakpointName] * 100}%` : null
  );
  return (
    <div
      css={css(
        mq({
          paddingBottom: bp,
          position: 'relative',
          width: '100%'
        })
      )}
    >
      <picture>
        {sources.map((source) => (
          <source {...source} key={source.srcSet} />
        ))}
        <img
          alt={img.alt}
          src={img.src}
          css={{
            display: 'block',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%'
          }}
        />
      </picture>
    </div>
  );
};

export type { ImgDef, SourceDef, PictureDef };
export { Image, Picture };
